import {useState, useEffect, useCallback, useRef} from 'react';
import {useDynamicPermissions} from './useDynamicPermissions';
import {usePermissions} from './usePermissions';
import {useAuth} from '../../auth/hooks/useAuth';
import {Permission} from '../models/permission.model';
import {Role} from '../models/role.model';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface UseRolePermissionsModalProps {
  open: boolean;
  role: Role | null;
}

interface UseRolePermissionsModalReturn {
  // State
  selectedPermissions: number[];
  initialPermissions: number[];
  assigning: boolean;
  loadingRolePermissions: boolean;
  permissionsLoading: boolean;
  allPermissions: Permission[];

  // Actions
  handlePermissionToggle: (permissionId: number) => void;
  handleSelectAll: (permissions: Permission[]) => void;
  handleDeselectAll: () => void;
  handleDeselectCategory: (categoryPermissions: Permission[]) => void;
  handleSavePermissions: () => Promise<void>;
  getResourceDisplayName: (resource: string) => string;
  getActionDisplayName: (action: string) => string;

  // Computed
  hasChanges: boolean;
  permissionsByCategory: Record<string, Permission[]>;
}

/**
 * Custom hook for managing role permissions modal state and logic
 * Separates business logic from UI components for better maintainability
 */
export const useRolePermissionsModal = ({
  open,
  role
}: UseRolePermissionsModalProps): UseRolePermissionsModalReturn => {
  // Hooks
  const {user} = useAuth();
  const {success: showSuccess, error: showError} = useNotification();
  const {
    permissions: allPermissions,
    loading: permissionsLoading,
    getResourceDisplayName,
    getActionDisplayName,
    refreshPermissions
  } = useDynamicPermissions();

  const {
    getRolePermissions,
    bulkAssignRolePermissions,
    bulkRemoveRolePermissions
  } = usePermissions();

  // State
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [initialPermissions, setInitialPermissions] = useState<number[]>([]);
  const [assigning, setAssigning] = useState<boolean>(false);
  const [loadingRolePermissions, setLoadingRolePermissions] = useState<boolean>(false);

  // Refs
  const hasLoadedPermissions = useRef(false);

  // Load current role permissions
  const loadRolePermissions = useCallback(async () => {
    if (!role) {
      return;
    }

    try {
      setLoadingRolePermissions(true);

      const rolePermissions = await getRolePermissions(role.id);

      if (!Array.isArray(rolePermissions)) {
        showError('خطأ في هيكل بيانات الصلاحيات');
        return;
      }

      // Extract permission IDs and filter out invalid ones
      const currentRolePermissionIds = rolePermissions.map((permission: Permission) => permission.id);
      const availablePermissionIds = (allPermissions || []).map(p => p.id);
      const validRolePermissionIds = currentRolePermissionIds.filter(id =>
        availablePermissionIds.includes(id)
      );

      setSelectedPermissions(validRolePermissionIds);
      setInitialPermissions(validRolePermissionIds);
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      StandardizedErrorHandler.getInstance().handleError(error); // For notifications/logging
    } finally {
      setLoadingRolePermissions(false);
    }
  }, [role, getRolePermissions, allPermissions]);

  // Load role permissions when modal opens
  useEffect(() => {
    if (open && role) {
      // Reset state when modal opens
      setSelectedPermissions([]);
      setInitialPermissions([]);
      hasLoadedPermissions.current = false;

      // Only refresh permissions if they're not already loaded
      if (allPermissions.length === 0) {
        refreshPermissions();
      }
    }

    // Cleanup when modal closes
    if (!open) {
      setSelectedPermissions([]);
      setInitialPermissions([]);
      hasLoadedPermissions.current = false;
    }
  }, [open, role, refreshPermissions]);

  // Load role permissions when permissions are available
  useEffect(() => {
    if (open && role && allPermissions.length > 0 && !hasLoadedPermissions.current) {
      hasLoadedPermissions.current = true;
      loadRolePermissions();
    }
  }, [allPermissions.length, open, role, loadRolePermissions]);

  // Group permissions by category
  const permissionsByCategory = useCallback(() => {
    const grouped: Record<string, Permission[]> = {};

    allPermissions.forEach(permission => {
      const category = getResourceDisplayName(permission.resource);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(permission);
    });

    return grouped;
  }, [allPermissions, getResourceDisplayName]);

  // Permission toggle handler
  const handlePermissionToggle = useCallback((permissionId: number) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }, []);

  // Select all permissions in a category
  const handleSelectAll = useCallback((permissions: Permission[]) => {
    const permissionIds = permissions.map(p => p.id);
    setSelectedPermissions(prev => [...new Set([...prev, ...permissionIds])]);
  }, []);

  // Deselect all permissions
  const handleDeselectAll = useCallback(() => {
    setSelectedPermissions([]);
  }, []);

  // Deselect permissions in a specific category
  const handleDeselectCategory = useCallback((categoryPermissions: Permission[]) => {
    const categoryPermissionIds = categoryPermissions.map(p => p.id);
    setSelectedPermissions(prev =>
      prev.filter(id => !categoryPermissionIds.includes(id))
    );
  }, []);

  // Save permissions
  const handleSavePermissions = useCallback(async () => {
    if (!role) {
      return;
    }

    try {
      setAssigning(true);

      // Calculate permissions to add and remove
      const permissionsToAdd = selectedPermissions.filter(id => !initialPermissions.includes(id));
      const permissionsToRemove = initialPermissions.filter(id => !selectedPermissions.includes(id));

      // Remove permissions first
      if (permissionsToRemove.length > 0) {
        await bulkRemoveRolePermissions({
          roleId: role.id,
          permissionIds: permissionsToRemove,
          reason: 'Permission removal via role management'
        });
      }

      // Add new permissions
      if (permissionsToAdd.length > 0) {
        await bulkAssignRolePermissions({
          roleId: role.id,
          permissionIds: permissionsToAdd,
          grantedBy: user?.id || 1 // Use current user ID or default to admin
        });
      }

      // Update initial permissions to reflect the new state
      setInitialPermissions(selectedPermissions);

      showSuccess('تم حفظ الصلاحيات بنجاح');
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      StandardizedErrorHandler.getInstance().handleError(error); // For notifications/logging
    } finally {
      setAssigning(false);
    }
  }, [role, selectedPermissions, initialPermissions, bulkAssignRolePermissions, bulkRemoveRolePermissions, user?.id]);

  // Check if there are changes
  const hasChanges = useCallback(() => {
    if (initialPermissions.length !== selectedPermissions.length) {
      return true;
    }

    const sortedInitial = [...initialPermissions].sort();
    const sortedSelected = [...selectedPermissions].sort();

    return !sortedInitial.every((id, index) => id === sortedSelected[index]);
  }, [initialPermissions, selectedPermissions]);

  return {
    // State
    selectedPermissions,
    initialPermissions,
    assigning,
    loadingRolePermissions,
    permissionsLoading,
    allPermissions,

    // Actions
    handlePermissionToggle,
    handleSelectAll,
    handleDeselectAll,
    handleDeselectCategory,
    handleSavePermissions,
    getResourceDisplayName,
    getActionDisplayName,

    // Computed
    hasChanges: hasChanges(),
    permissionsByCategory: permissionsByCategory()
  };
};
